/**
 * Gate Report Service — real API adapter.
 *
 * Fetches gate artifacts from existing API endpoints and normalizes them
 * into canonical Issue[] and status shapes for the UI.
 *
 * Endpoints used:
 *   Structural (Java Validation):
 *     GET /java-validation/results/json/{jobId}/java_validation_report.json
 *
 *   Compile (Pre-Flight):
 *     GET /pre-flight/results/json/{jobId}/compile_result.json
 *     GET /pre-flight/results/json/{jobId}/compile_failure_snippets.json  (optional)
 *
 * Both endpoints return 404 when artifacts are not yet available — treated as
 * empty Issue[] and shown as a real "no artifacts" state in the UI.
 *
 * No mock data. No USE_MOCK_* flags.
 */

import { getAuthHeaders } from '../nodeExecutors/authHelper'
import {
  normalizeJavaValidationReport,
  normalizeCompileResult,
  deriveGateStatus,
} from './gateIssueNormalizer'

const hdrs = () => getAuthHeaders({ 'Content-Type': 'application/json' })

/**
 * Fetch and normalize gate findings for a given job.
 *
 * @param {string} jobId
 * @param {'structural'|'compile'} gateType
 * @param {string} apiBase
 * @returns {Promise<Array>} Normalized Issue[]
 */
export async function fetchGateFindings(jobId, gateType, apiBase) {
  if (gateType === 'structural') {
    const res = await fetch(
      `${apiBase}/java-validation/results/json/${jobId}/java_validation_report.json`,
      { headers: hdrs() }
    )
    if (res.status === 404) return []
    if (!res.ok) throw new Error(`Java Validation report fetch failed: ${res.status}`)
    return normalizeJavaValidationReport(await res.json())
  }

  if (gateType === 'compile') {
    const res = await fetch(
      `${apiBase}/pre-flight/results/json/${jobId}/compile_result.json`,
      { headers: hdrs() }
    )
    if (res.status === 404) return []
    if (!res.ok) throw new Error(`Pre-Flight compile result fetch failed: ${res.status}`)
    const compileResult = await res.json()

    // Optional: compile_failure_snippets.json provides ±50 line context per error
    let snippets = null
    try {
      const sr = await fetch(
        `${apiBase}/pre-flight/results/json/${jobId}/compile_failure_snippets.json`,
        { headers: hdrs() }
      )
      if (sr.ok) snippets = await sr.json()
    } catch (_) {
      // Snippets are optional — silently skip if missing or network error
    }

    return normalizeCompileResult(compileResult, snippets)
  }

  if (gateType === 'package') {
    const res = await fetch(
      `${apiBase}/java-packaging/results/json/${jobId}/validation_report.json`,
      { headers: hdrs() }
    )
    if (res.status === 404) return []
    if (!res.ok) throw new Error(`Java Packaging validation report fetch failed: ${res.status}`)
    const valReport = await res.json()
    const pseudoCompile = {
      success: valReport.compile?.compile_status === 'PASSED',
      errors_preview: valReport.compile?.errors_preview || [],
    }

    let snippets = null
    try {
      const sr = await fetch(
        `${apiBase}/java-packaging/results/json/${jobId}/compile_failure_snippets.json`,
        { headers: hdrs() }
      )
      if (sr.ok) snippets = await sr.json()
    } catch (_) {
      // Snippets are optional — silently skip if missing or network error
    }

    return normalizeCompileResult(pseudoCompile, snippets, 'java', 'package')
  }

  return []
}

/**
 * Derive gate status (PASSED / WARNINGS / BLOCKED) from live artifacts.
 * Fetches findings then derives counts — single source of truth.
 *
 * @param {string} jobId
 * @param {'structural'|'compile'} gateType
 * @param {string} apiBase
 * @returns {Promise<{ status: string, counts: object }>}
 */
export async function fetchGateStatus(jobId, gateType, apiBase) {
  const findings = await fetchGateFindings(jobId, gateType, apiBase)
  return deriveGateStatus(findings)
}

/**
 * fetchSourceContext — not used in real flow.
 * Editor loads COBOL/Java files directly via /editor/* endpoints.
 */
export async function fetchSourceContext() {
  return null
}

/**
 * applyAutoFix — placeholder; no deterministic auto-fix implemented yet.
 */
export async function applyAutoFix() {
  return { success: false, message: 'Auto-fix not yet implemented' }
}

/**
 * markTodo — placeholder; not yet wired to a persistence endpoint.
 */
export async function markTodo() {
  return { success: false, message: 'Mark TODO not yet implemented' }
}

// Re-export so callers can derive status from pre-fetched findings without a second round-trip.
export { deriveGateStatus }
